import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Trash2, CalendarCheck, UserCheck } from "lucide-react";
import { api } from "@/lib/api";
import { ALL_STATUSES } from "@/lib/fields";
import { GuestNameAutocomplete } from "@/components/GuestNameAutocomplete";
import { MultiDatePicker } from "@/components/MultiDatePicker";

const normName = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Lightweight popup to create OR edit a tentative Future Request (guest + date(s) + notes + status).
// Stored as a normal tour record so it shows everywhere; stays a request until converted.
export const FutureRequestDialog = ({ open, onOpenChange, onCreated, request = null, onConvert, onDelete }) => {
  const navigate = useNavigate();
  const isEdit = !!request?.id;
  const [name, setName] = useState("");
  const [dates, setDates] = useState([]);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("Future (Request)");
  const [saving, setSaving] = useState(false);
  const [knownNames, setKnownNames] = useState([]);

  const reset = () => { setName(""); setDates([]); setNotes(""); setStatus("Future (Request)"); };

  // Load existing guest names so a new request can be actively matched to a returning guest.
  useEffect(() => {
    if (!open) return;
    api.guestNames().then((n) => setKnownNames(Array.isArray(n) ? n : [])).catch(() => setKnownNames([]));
  }, [open]);

  // For a NEW request, an exact (normalized) name match means this is a returning guest.
  const isReturningGuest = !isEdit && !!name.trim() && knownNames.some((n) => normName(n) === normName(name));

  // Prefill for edit / reset for create — covers Cancel, Esc, overlay, and programmatic close.
  useEffect(() => {
    if (!open) { reset(); return; }
    if (request) {
      setName(request.guest_name || "");
      const td = Array.isArray(request.tour_date) ? request.tour_date : request.tour_date ? [request.tour_date] : [];
      setDates(td);
      setNotes(request.notes || "");
      setStatus(request.status_override || request.booking_status || "Future (Request)");
    } else {
      reset();
    }
  }, [open, request]);

  const save = async () => {
    if (!name.trim()) { toast.error("Add a guest name"); return; }
    setSaving(true);
    try {
      const fields = { guest_name: name.trim(), tour_date: dates, notes: notes.trim(), status_override: status };
      if (!isEdit && isReturningGuest) fields.return_trip = true;
      const saved = isEdit ? await api.updateGuest(request.id, fields, {}) : await api.createGuest(fields);
      toast.success(isEdit ? "Request updated" : (isReturningGuest ? "Future request added — marked as a returning guest" : "Future request added"));
      onCreated?.(saved);
      onOpenChange(false);
    } catch {
      toast.error("Couldn't save the request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-zinc-800 bg-zinc-950 sm:max-w-md" data-testid="fr-create-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-50">{isEdit ? "Edit future request" : "New future request"}</DialogTitle>
          <DialogDescription className="text-zinc-400">A tentative tour — just the essentials. Convert it later when a date is confirmed.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-600 uppercase tracking-wide text-zinc-500">Guest name</label>
            <GuestNameAutocomplete
              value={name}
              onChange={setName}
              onOpen={isEdit ? () => { onOpenChange(false); navigate(`/guest/${request.id}`); } : undefined}
              testid="fr-create-name"
            />
            {isReturningGuest && (
              <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-600 text-emerald-400" data-testid="fr-returning-hint">
                <UserCheck className="h-3.5 w-3.5" /> Returning guest — will be marked as a Return
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-600 uppercase tracking-wide text-zinc-500">Requested date(s)</label>
            <MultiDatePicker value={dates} onChange={setDates} testid="fr-create-date-input" />
          </div>

          <div>
            <label className="mb-1 block text-xs font-600 uppercase tracking-wide text-zinc-500">Notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="What did they ask for? Any preferences…" className="border-zinc-800 bg-zinc-900 text-zinc-100" data-testid="fr-create-notes" />
          </div>

          <div>
            <label className="mb-1 block text-xs font-600 uppercase tracking-wide text-zinc-500">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="border-zinc-800 bg-zinc-900 text-zinc-100" data-testid="fr-create-status"><SelectValue /></SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-950 text-zinc-200">
                {ALL_STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {isEdit ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => { onDelete?.(request); onOpenChange(false); }} className="border-red-900/50 bg-transparent text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="fr-create-delete"><Trash2 className="h-4 w-4" /></Button>
              {onConvert && <Button variant="outline" onClick={() => { onConvert(request); onOpenChange(false); }} className="border-[#9B2226]/50 bg-[#9B2226]/10 text-[#E0575B] hover:bg-[#9B2226]/20" data-testid="fr-create-convert"><CalendarCheck className="mr-1 h-4 w-4" /> Convert</Button>}
            </div>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100" data-testid="fr-create-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="fr-create-save">{saving ? "Saving…" : isEdit ? "Save changes" : "Create request"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

